import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function DynamicParamsPage(): Node {
  return html`
    <div>
      <h1>Dynamic Parameters</h1>
      <p>Define URL segments that capture values at runtime using the <code>:param</code> syntax. Captured values are passed to the view function as a params object.</p>

      <h2>Defining Dynamic Routes</h2>
      ${CodeBlock(`import { Router, html } from 'onefold';

const App = Router([
  { path: '/users/:id', view: (params) => UserProfile(params) },
  { path: '/posts/:slug', view: (params) => BlogPost(params) },
  { path: '/org/:orgId/team/:teamId', view: (params) => TeamPage(params) },
]);`)}

      <h2>Accessing Parameters</h2>
      <p>The <code>params</code> object is a plain key-value map of captured segments:</p>
      ${CodeBlock(`function UserProfile(params: { id: string }): Node {
  return html\`
    <div>
      <h2>User: \${params.id}</h2>
      <!-- fetch user data using params.id -->
    </div>
  \`;
}

// URL: /users/42 → params = { id: '42' }
// URL: /users/abc → params = { id: 'abc' }`)}

      <h2>Multiple Parameters</h2>
      ${CodeBlock(`function TeamPage(params: { orgId: string; teamId: string }): Node {
  return html\`
    <div>
      <h2>Org: \${params.orgId} / Team: \${params.teamId}</h2>
    </div>
  \`;
}

// URL: /org/acme/team/engineering
// params = { orgId: 'acme', teamId: 'engineering' }`)}

      ${Callout('All param values are strings. Parse numbers yourself: parseInt(params.id, 10).')}

      <h2>Combined with Resource</h2>
      <p>Use params with <code>createResource</code> for reactive data fetching:</p>
      ${CodeBlock(`import { createSignal, createResource, html } from 'onefold';

function UserProfile(params: { id: string }): Node {
  const userId = createSignal(params.id);
  const user = createResource(userId, async (id) => {
    const res = await fetch(\`/api/users/\${id}\`);
    return res.json();
  });

  return html\`
    <div>
      <h2>\${() => user.data()?.name ?? 'Loading...'}</h2>
      <p>\${() => user.data()?.email ?? ''}</p>
    </div>
  \`;
}`)}

      <h2>Try It</h2>
      <p>Click different user links to see the dynamic <code>:id</code> param change:</p>

      ${Playground(`import { html, mount, Router, navigate, currentRoute, Link, configureRouter } from 'onefold';

configureRouter({ hash: true });

function UserProfile(params) {
  const users = {
    '1': { name: 'Alice Johnson', role: 'Engineer', avatar: 'A' },
    '2': { name: 'Bob Smith', role: 'Designer', avatar: 'B' },
    '3': { name: 'Charlie Brown', role: 'Product Manager', avatar: 'C' },
  };
  const user = users[params.id] || { name: 'Unknown', role: 'N/A', avatar: '?' };

  return html\`
    <div style="padding:16px">
      <div style="display:flex;align-items:center;gap:12px">
        <div style="width:48px;height:48px;border-radius:50%;background:#4f46e5;color:white;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:bold">\${user.avatar}</div>
        <div>
          <h3 style="margin:0">\${user.name}</h3>
          <p style="margin:0;font-size:13px;color:#666">\${user.role} — ID: \${params.id}</p>
        </div>
      </div>
    </div>
  \`;
}

function UserList() {
  return html\`
    <div style="padding:16px">
      <h3>Select a User</h3>
      <div style="display:flex;gap:8px;margin-top:8px">
        <button onclick=\${() => navigate('/users/1')}>Alice</button>
        <button onclick=\${() => navigate('/users/2')}>Bob</button>
        <button onclick=\${() => navigate('/users/3')}>Charlie</button>
        <button onclick=\${() => navigate('/users/999')}>Unknown</button>
      </div>
    </div>
  \`;
}

function App() {
  return html\`
    <div>
      <div style="border:1px solid #e5e7eb;border-radius:8px;min-height:100px">
        \${Router([
          { path: '/', view: () => UserList() },
          { path: '/users/:id', view: (params) => UserProfile(params) },
        ], () => UserList())}
      </div>
      <div style="margin-top:8px;display:flex;justify-content:space-between;font-size:12px;color:#666">
        <span>Route: \${() => currentRoute()}</span>
        <button onclick=\${() => navigate('/')} style="font-size:12px;padding:2px 8px">Reset</button>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Dynamic Route Params — :id')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/data/resource">Resource</a> — reactive async data fetching</li>
        <li><a href="/routing/router">Router</a> — client-side routing overview</li>
      </ul>
    </div>
  `;
}
